import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical } from 'lucide-react';
import Iconify from 'src/components/Iconify';
import { FUNIL_DESIGN as D } from './FunilComponent.design';

import v2_actionGetLayoutConfig from 'src/actions/v2/crm/v2_actionGetLayoutConfig';
import v2_actionSalvarLayoutConfig from 'src/actions/v2/crm/v2_actionSalvarLayoutConfig';

export default function TabLayoutConfig({ cliente }) {
  const { t } = useTranslation('settings');

  const DEFAULT_SECOES = [
    { key: 'negociacao', label: t('funnel.core.layout.negotiation', 'Negociação'), posicao: 0 },
    { key: 'propostas', label: t('funnel.core.layout.proposals', 'Propostas'), posicao: 1 },
    { key: 'tarefas', label: t('funnel.core.layout.tasks', 'Tarefas'), posicao: 2 },
    { key: 'contatos', label: t('funnel.core.layout.contacts', 'Contatos'), posicao: 3 },
    { key: 'atividades', label: t('funnel.core.layout.activities', 'Atividades'), posicao: 4 },
    { key: 'comentarios', label: t('funnel.core.layout.comments', 'Comentários'), posicao: 5 },
    { key: 'campos_personalizados', label: t('funnel.core.layout.customFields', 'Campos Personalizados'), posicao: 6 },
    { key: 'realestate', label: t('funnel.core.layout.realEstate', 'Imóvel'), posicao: 7 },
  ];

  const MODO_OPTIONS = [
    {
      value: 'accordion',
      label: 'Accordion',
      desc: t('funnel.core.layout.accordionDesc', 'Seções expansíveis empilhadas verticalmente'),
    },
    {
      value: 'tabs',
      label: t('funnel.core.layout.tabsLabel', 'Tabs horizontais'),
      desc: t('funnel.core.layout.tabsDesc', 'Navegação por abas horizontais no topo'),
    },
  ];

  const [modo, setModo] = useState('accordion');
  const [secoes, setSecoes] = useState(DEFAULT_SECOES);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    buscarConfig();
  }, []);

  async function buscarConfig() {
    try {
      const result = await v2_actionGetLayoutConfig(cliente._id);
      if (result.status === 200 && !result.data?.error) {
        const data = result.data.data;
        if (data.modo_visualizacao) setModo(data.modo_visualizacao);
        if (data.ordem_secoes?.length > 0) {
          const merged = DEFAULT_SECOES.map((def) => {
            const found = data.ordem_secoes.find((s) => s.key === def.key);
            return found ? { ...def, posicao: found.posicao } : def;
          }).sort((a, b) => a.posicao - b.posicao);
          setSecoes(merged);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function handleSalvar() {
    try {
      setSalvando(true);
      const secoesComPosicao = secoes.map((s, i) => ({ key: s.key, label: s.label, posicao: i }));
      await v2_actionSalvarLayoutConfig(cliente._id, {
        modo_visualizacao: modo,
        ordem_secoes: secoesComPosicao,
      });
      setSalvo(true);
      setTimeout(() => setSalvo(false), 2000);
    } catch (error) {
      console.log(error);
    } finally {
      setSalvando(false);
    }
  }

  function handleDragStart(index) {
    setDragIndex(index);
  }

  function handleDragOver(e, index) {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    const newSecoes = [...secoes];
    const item = newSecoes.splice(dragIndex, 1)[0];
    newSecoes.splice(index, 0, item);
    setSecoes(newSecoes);
    setDragIndex(index);
  }

  function handleDragEnd() {
    setDragIndex(null);
  }

  function moveItem(index, direction) {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= secoes.length) return;
    const newSecoes = [...secoes];
    [newSecoes[index], newSecoes[newIndex]] = [newSecoes[newIndex], newSecoes[index]];
    setSecoes(newSecoes);
  }

  return (
    <div className={D.stack} style={{ fontFamily: D.fontStack }}>
      <div className={D.sectionHeader}>
        <div className={D.sectionHeaderMain}>
          <h2 className={D.sectionHeaderTitle}>
            {t('funnel.core.layout.viewModeTitle', 'Modo de Visualização')}
          </h2>
          <p className={D.sectionHeaderSub}>
            {t('funnel.core.layout.viewModeSubtitle', 'Escolha como as seções do card de negócio serão exibidas')}
          </p>
        </div>
        <button
          type="button"
          onClick={handleSalvar}
          disabled={salvando}
          className={D.primaryButton}
        >
          {salvando
            ? t('funnel.core.layout.saving', 'Salvando...')
            : salvo
              ? t('funnel.core.layout.saved', 'Salvo!')
              : t('funnel.core.layout.saveButton', 'Salvar configuração')}
        </button>
      </div>

      <div className={D.card}>
        <div className={D.choiceGrid}>
          {MODO_OPTIONS.map((option) => {
            const on = modo === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setModo(option.value)}
                className={`${D.choiceCard} ${on ? D.choiceCardOn : D.choiceCardOff}`}
              >
                <span className={`${D.choiceTitle} ${on ? D.choiceTitleOn : D.choiceTitleOff}`}>
                  {option.label}
                </span>
                <p className={D.choiceHelp}>{option.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className={D.sectionHeader}>
        <div className={D.sectionHeaderMain}>
          <h2 className={D.sectionHeaderTitle}>
            {t('funnel.core.layout.orderTitle', 'Ordem das Seções')}
          </h2>
          <p className={D.sectionHeaderSub}>
            {t('funnel.core.layout.orderSubtitle', 'Arraste ou use as setas para reordenar as seções do card')}
          </p>
        </div>
      </div>

      <div className={D.card}>
        <div className="flex flex-col gap-1.5">
          {secoes.map((secao, index) => (
            <div
              key={secao.key}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`${D.sortItem} ${dragIndex === index ? D.sortItemDragging : ''}`}
            >
              <GripVertical size={14} className="text-[#a1a1aa] shrink-0" />
              <span className={D.sortIndex}>{index + 1}</span>
              <span className="text-[14px] font-medium text-[#18181b] flex-1">{secao.label}</span>
              <div className="flex gap-0.5">
                <button
                  type="button"
                  onClick={() => moveItem(index, -1)}
                  disabled={index === 0}
                  className="p-1 rounded-md hover:bg-[#f4f4f7] text-[#a1a1aa] hover:text-[#18181b] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Iconify icon="eva:arrow-up-outline" width={14} />
                </button>
                <button
                  type="button"
                  onClick={() => moveItem(index, 1)}
                  disabled={index === secoes.length - 1}
                  className="p-1 rounded-md hover:bg-[#f4f4f7] text-[#a1a1aa] hover:text-[#18181b] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Iconify icon="eva:arrow-down-outline" width={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
